<template>
  <span
    class="app-status-badge"
    :class="[
      `app-status-badge--${tone}`,
      {
        'app-status-badge--new': design === 'new',
        'app-status-badge--compact': compact
      }
    ]"
    :style="customStyle"
  >
    <span v-if="dot" class="app-status-badge__dot" aria-hidden="true" />
    <slot>{{ label }}</slot>
  </span>
</template>

<script>
const DEFAULT_BADGE_COLOR = '#667085'
const MINIMUM_TEXT_CONTRAST = 4.5

function parseColor(value) {
  const color = String(value || '').trim()
  const shortHex = color.match(/^#([0-9a-f]{3})$/i)
  const longHex = color.match(/^#([0-9a-f]{6})$/i)

  if (shortHex) {
    return shortHex[1]
      .split('')
      .map(channel => parseInt(`${channel}${channel}`, 16))
  }

  if (longHex) {
    return [
      parseInt(longHex[1].slice(0, 2), 16),
      parseInt(longHex[1].slice(2, 4), 16),
      parseInt(longHex[1].slice(4, 6), 16)
    ]
  }

  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (!rgb) return null
  return rgb.slice(1, 4).map(channel => Math.min(255, Number(channel)))
}

function relativeLuminance(channels) {
  const normalized = channels.map(channel => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return (0.2126 * normalized[0]) + (0.7152 * normalized[1]) + (0.0722 * normalized[2])
}

function accessibleSolidColor(value) {
  let channels = parseColor(value) || parseColor(DEFAULT_BADGE_COLOR)

  while (1.05 / (relativeLuminance(channels) + 0.05) < MINIMUM_TEXT_CONTRAST) {
    channels = channels.map(channel => Math.max(0, Math.round(channel * 0.88)))
  }

  return `#${channels.map(channel => channel.toString(16).padStart(2, '0')).join('')}`
}

export default {
  name: 'AppStatusBadge',
  props: {
    label: {
      type: String,
      default: ''
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: value => ['neutral', 'blue', 'green', 'amber', 'red'].includes(value)
    },
    color: {
      type: String,
      default: ''
    },
    preserveColor: {
      type: Boolean,
      default: false
    },
    dot: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    design: {
      type: String,
      required: true,
      validator: value => value === 'new'
    }
  },
  computed: {
    customStyle() {
      if (!this.color) return null
      const backgroundColor = this.preserveColor ? (parseColor(this.color) ? this.color : DEFAULT_BADGE_COLOR) : accessibleSolidColor(this.color)
      return {
        borderColor: backgroundColor,
        backgroundColor,
        color: '#fff'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-status-badge--new {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid #667085;
  border-radius: $ui-control-radius;
  background-color: #667085;
  color: #fff;
  font-family: $ui-font-heading;
  font-size: $ui-font-size-metadata;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;

  &.app-status-badge--blue {
    border-color: var(--ui-info, var(--ui-accent, #{$ui-blue}));
    background-color: var(--ui-info, var(--ui-accent, #{$ui-blue}));
  }

  &.app-status-badge--green {
    border-color: var(--ui-success, #{$ui-green});
    background-color: var(--ui-success, #{$ui-green});
    color: var(--ui-success-ink, #fff);
  }

  &.app-status-badge--red {
    border-color: var(--ui-danger, #{$ui-red});
    background-color: var(--ui-danger, #{$ui-red});
  }

  &.app-status-badge--amber {
    border-color: var(--ui-amber, #{$ui-amber});
    background-color: var(--ui-amber, #{$ui-amber});
    color: var(--ui-amber-ink, #fff);
  }

  &.app-status-badge--compact {
    min-height: 24px;
    padding: 2px $ui-space-8;
    font-size: $ui-font-size-metadata;
    line-height: 18px;
  }
}

.app-status-badge__dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, .9);
}
</style>
